import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Book, Heart, ShieldCheck, Pill, Flask, Users } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const PSPGuidesPage = () => {
  const navigate = useNavigate();
  const [selectedGuide, setSelectedGuide] = useState(null);

  const guides = [
    {
      id: 'pnp-101',
      emoji: '🎉',
      title: 'Chemsex / PnP 101',
      subtitle: 'Quick Guide',
      icon: ShieldCheck,
      color: 'from-purple-500 to-pink-500',
      content: {
        intro: "Chemsex (aka PnP / Party & Play) is using drugs to make sex last longer, feel more intense, or lower boundaries",
        sections: [
          {
            title: 'Common Chems',
            items: ['Tina (crystal meth)', 'GHB / GBL', 'MDMA', 'Ketamine', 'Poppers']
          },
          {
            title: 'Common Ways People Use',
            items: ['Smoking', 'Snorting', 'Booty bumping (boofing)', 'Slamming (injecting)']
          },
          {
            title: 'Main Risks',
            items: [
              'Overheating & dehydration',
              'Overamping or overdose',
              'HIV / STIs / Hep C',
              'Mental health crashes after',
              'Sleep loss, anxiety, paranoia'
            ]
          },
          {
            title: 'Harm Reduction = Self-Love 💗',
            subtitle: 'Before / During:',
            items: [
              'Set limits (time, dose, chems)',
              "Don't mix chems if you can",
              'Drink water (not too much)',
              'Eat small snacks',
              'Use your own supplies',
              'Talk about HIV status, PrEP, condoms'
            ]
          },
          {
            title: 'If Someone Overamps',
            items: [
              'Cool them down',
              'Quiet space',
              'Water sips',
              'Reassure them',
              'Watch breathing & heart rate'
            ]
          },
          {
            title: 'After Care',
            items: [
              'Sleep',
              'Shower & oral care',
              'Gentle food',
              'Be kind to your brain',
              'No shame — rest is recovery'
            ]
          }
        ]
      }
    },
    {
      id: 'prep-partying',
      emoji: '💊',
      title: 'How Partying Affects PrEP',
      subtitle: 'P=P: Protection = Pills',
      icon: Pill,
      color: 'from-cyan-500 to-blue-500',
      content: {
        intro: "PrEP works — even if you party — as long as you take it consistently.",
        sections: [
          {
            title: '1. Forgetting Doses',
            text: 'Partying, long nights, or using substances can make you forget to take your PrEP. Missing doses = less protection.'
          },
          {
            title: '2. Vomiting or Diarrhea',
            text: 'If you throw up soon after taking PrEP, your body might not absorb it, this can lower protection.'
          },
          {
            title: '3. Routine Disruption',
            text: 'Sleeping all day, traveling, or crashing after a party can mess up your daily pill routine.'
          },
          {
            title: 'What Partying Does NOT Do',
            items: [
              'Party drugs do NOT cancel PrEP',
              'Alcohol does NOT stop PrEP from working',
              'Most party drugs do NOT interact directly with PrEP meds'
            ]
          },
          {
            title: 'Harm Reduction Tips',
            items: [
              'Set a daily alarm for your PrEP',
              'Keep PrEP in your bag when partying',
              'Take it at the same time every day (or before the party)',
              'If you vomit within 1 hour of taking it, take another pill',
              'If you miss doses often, long act injection will be a good option'
            ]
          }
        ],
        footer: 'P = P: Protection = pills + routine + self-care.'
      }
    },
    {
      id: 'g-safety',
      emoji: '⚗️',
      title: 'Knowing Your "G"',
      subtitle: 'BDO, GBL, or GHB?',
      icon: Flask,
      color: 'from-orange-500 to-red-500',
      content: {
        intro: "Not all \"G\" is the same. Folks often use the word \"G\" to mean BDO, GBL, or GHB, but these are different substances with different risks.",
        warning: "This information is based on personal experience. It is not professional or medical advice. G can be dangerous if misused.",
        sections: [
          {
            title: 'BDO (Butanediol)',
            items: [
              'Usually feels a bit oily or thicker',
              'Slower to hit for some people',
              'Can feel mild at first, then suddenly hit later',
              'Recommended starting: 2.5 ml',
              'BDO can sneak up on you. Feeling "nothing yet" doesn\'t mean more is safe.'
            ]
          },
          {
            title: 'GBL (Gamma-Butyrolactone)',
            items: [
              'More watery',
              'Can sting the mouth',
              'Can damage plastic',
              'Hits faster than BDO',
              'Stronger and easier to overdo',
              'Standard dose: 2.0 ml (experienced: 1.5 ml)',
              'Be cautious not to redose too soon'
            ]
          },
          {
            title: 'GHB (Gamma-Hydroxybutyrate)',
            items: [
              'Watery like GBL',
              'Often tastes salty',
              'Strength can vary a lot depending on how it was made',
              'Hard to know what you\'re actually getting',
              'You often don\'t know the strength. That makes it unpredictable.'
            ]
          },
          {
            title: 'Big Risks People Underestimate',
            text: 'The line between \"feels nice\" and \"gone unconscious\" can be very thin.',
            items: [
              'Not knowing which type of G you have',
              'Different strengths between batches',
              'Redosing too soon',
              'Using when tired, dehydrated, or not eating',
              'Mixing with alcohol, benzos, or other downers',
              'Party environments where people lose track of time'
            ]
          },
          {
            title: 'Safety Basics',
            items: [
              'Avoid mixing G with alcohol or other depressants',
              'Stay with people you trust',
              'Keep track of time (alarms help)',
              'Drink water, eat, and rest',
              'Don\'t leave someone alone if they\'re fading out',
              'If someone is hard to wake, breathing is slow, or lips look blue, get medical help'
            ]
          }
        ],
        footer: 'Harm reduction = self-respect + community care.'
      }
    },
    {
      id: 'self-love',
      emoji: '💙',
      title: 'Harm Reduction = Self-Love',
      subtitle: 'You Deserve Care',
      icon: Heart,
      color: 'from-pink-500 to-rose-500',
      content: {
        quotes: [
          "PnP doesn't cancel your worth. Harm reduction is self-love.",
          "If you party and play, you still deserve care. Harm reduction = self-love.",
          "Using it doesn't mean you don't care about yourself. Choosing harm reduction means you do. Self-love lives here.",
          "In PnP spaces, harm reduction means: Hydration. Breaks. Testing. Boundaries. Aftercare. That's self-love.",
          "PnP with intention. Care for your body. Check in with your mind. Harm reduction is self-love."
        ]
      }
    },
    {
      id: 'psp-project',
      emoji: '🏳️‍🌈',
      title: 'Building Safer Communities',
      subtitle: 'PSP Project',
      icon: Users,
      color: 'from-purple-500 via-pink-500 to-cyan-500',
      content: {
        intro: "The PSP Project (Party Smart Project) is all about creating safer spaces for 2SGBTQ+ folks who party and play, by mixing harm reduction with community connection.",
        sections: [
          {
            title: 'Why Community Workshops Matter',
            items: [
              'Sense of belonging: When people feel connected, they\'re more likely to check in on each other',
              'Safety knowledge: From substance use limits, to consent, to first aid',
              'Support culture: Instead of silence or shame, we create trust and care'
            ]
          },
          {
            title: 'What Makes a Workshop Work',
            items: [
              'Interactive activities like role-plays and group talks',
              'Community members share opinions and work together',
              'Guest voices: Health workers, community leaders',
              'Resource sharing: Safer use supplies, local supports'
            ]
          },
          {
            title: 'Personal Responsibility + Peer Support',
            items: [
              'Know your limits',
              'Use harm reduction supplies',
              'Check on your friends',
              'Step in if someone\'s not safe'
            ]
          }
        ],
        footer: 'Party Smart. Stay Safe. Stay Connected.'
      }
    }
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedGuide ? setSelectedGuide(null) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">📚</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedGuide ? guides.find(g => g.id === selectedGuide)?.title : 'PSP Guides'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedGuide ? (
          <>
            <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
              <div className="text-5xl mb-3">✨</div>
              <h2 className="text-2xl font-bold mb-3">PSP Community Guides</h2>
              <p className="text-white/90 leading-relaxed">
                Real talk, no judgment. Learn from the community about safer partying, harm reduction, and taking care of yourself and others. 💜
              </p>
            </div>

            <div className="space-y-4">
              {guides.map((guide) => {
                const Icon = guide.icon;
                return (
                  <button
                    key={guide.id}
                    onClick={() => setSelectedGuide(guide.id)}
                    data-testid={`guide-${guide.id}`}
                    className="w-full group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${guide.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                    <div className="relative z-10 flex items-start">
                      <div className="text-5xl mr-4 emoji-bounce">{guide.emoji}</div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                          {guide.title}
                        </h3>
                        <p className="text-sm text-[rgb(var(--text-secondary))]">
                          {guide.subtitle}
                        </p>
                      </div>
                      <Icon size={24} weight="duotone" className="text-[rgb(var(--primary))] group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {guides.find(g => g.id === selectedGuide)?.content && (() => {
              const guide = guides.find(g => g.id === selectedGuide);
              const content = guide.content;
              
              return (
                <>
                  {content.intro && (
                    <div className={`p-6 bg-gradient-to-br ${guide.color} rounded-3xl text-white`}>
                      <p className="leading-relaxed text-lg">
                        {content.intro}
                      </p>
                    </div>
                  )}

                  {content.warning && (
                    <div className="p-4 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/30 rounded-2xl">
                      <p className="text-sm text-[rgb(var(--text-secondary))]">
                        ⚠️ {content.warning}
                      </p>
                    </div>
                  )}

                  {content.quotes && (
                    <div className="space-y-4">
                      {content.quotes.map((quote, idx) => (
                        <div key={idx} className="p-6 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
                          <p className="text-[rgb(var(--text-primary))] leading-relaxed italic">
                            "{quote}"
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {content.sections && content.sections.map((section, idx) => (
                    <div key={idx} className="space-y-3">
                      <h3 className="text-xl font-bold text-[rgb(var(--text-primary))]">
                        {section.title}
                      </h3>
                      {section.subtitle && (
                        <p className="text-[rgb(var(--text-secondary))] font-semibold">
                          {section.subtitle}
                        </p>
                      )}
                      {section.text && (
                        <p className="text-[rgb(var(--text-secondary))] leading-relaxed">
                          {section.text}
                        </p>
                      )}
                      {section.items && (
                        <ul className="space-y-2">
                          {section.items.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start p-3 bg-[rgb(var(--surface))] rounded-xl border border-white/5">
                              <span className="text-[rgb(var(--primary))] mr-2">•</span>
                              <span className="text-[rgb(var(--text-secondary))] leading-relaxed">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  {content.footer && (
                    <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center">
                      <p className="text-[rgb(var(--text-primary))] font-bold">
                        {content.footer}
                      </p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
};

export default PSPGuidesPage;
